// App.js 

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import api from './api';

import ErrorBoundary from './ErrorBoundary';
import { I18nProvider } from './i18n/I18nProvider';
import './i18n';

import { ThemeProvider } from './context/ThemeContext';
import './styles/theme.css';

// Context Providers
import { PostProvider } from './context/PostContext';
import { SavedPostsProvider } from './context/SavedPostsContext';
import { TipListProvider } from './context/TipListContext';
import { MarketProvider } from './context/MarketContext';
import { ProfileProvider } from './context/ProfileContext';
import { UserPostProvider } from './context/UserPostContext';
import { CartProvider } from './CartContext';
import { MerchantStoreProvider } from './context/MerchantStoreContext';
import { LikedProductsProvider } from './context/LikedProductsContext';
import { SavedProductsProvider } from './context/SavedProductsContext';
import { DiscoveryProvider } from './context/DiscoveryContext';
import { ActiveStoresProvider } from './context/ActiveStoresContext';
import { BannerProvider } from './context/BannerContext';
import { EscrowBannerProvider } from './context/EscrowBannerContext';
import { DepartmentProvider } from './context/DepartmentContext';

import { SuggestedStoriesDiscoveryProvider } from './context/SuggestedStoriesDiscoveryContext';

import { NotificationProvider } from './context/NotificationContext';
import { NewsProvider } from './context/NewsContext';
import { PiAppsDiscoveryProvider } from './context/PiAppsDiscoveryContext';
import { MessageUnreadProvider } from './context/MessageUnreadContext';

// Global Styles
import './App.css';
import './styles/globalFixes.css';

// Admin Screens
import AdminRoute from './components/AdminRoute';
import AdminDashboard from './screens/Admin/AdminDashboard';
import DisputeManager from './screens/Admin/DisputeManager';
import DisputeDetailScreen from './screens/Admin/DisputeDetailScreen';
import ReportManager from './screens/Admin/ReportManager';
import ReportDetail from './screens/Admin/ReportDetail';
import MerchantManager from './screens/Admin/MerchantManager';
import MerchantDetail from './screens/Admin/MerchantDetail';
import UserManager from './screens/Admin/UserManager';
import UserDetail from './screens/Admin/UserDetail';
import Metrics from './screens/Admin/Metrics';
import AppealManager from './screens/Admin/AppealManager';
import AppealDetail from './screens/Admin/AppealDetail';
import FeedbackManager from './screens/Admin/FeedbackManager';
import FeedbackDetail from './screens/Admin/FeedbackDetail';
import SponsorshipManager from './screens/Admin/SponsorshipManager';
import SponsorshipDetail from './screens/Admin/SponsorshipDetail';
import FeedConfigManager from './screens/Admin/FeedConfigManager';
import AdminWallet from './screens/Admin/AdminWallet';
import AdminTestPaymentScreen from './screens/Admin/AdminTestPaymentScreen';
import WithdrawalManager from './screens/Admin/WithdrawalManager';
import WithdrawalDetail from './screens/Admin/WithdrawalDetail';
import GraphsManager from './screens/Admin/GraphsManager';
import GraphsDetail from './screens/Admin/GraphsDetail';
import ActiveWalletManager from './screens/Admin/ActiveWalletManager';
import LangCountryStats from './screens/Admin/LangCountryStats';
import PlansManager from './screens/Admin/PlansManager';
import ActivityGraphDetail from './screens/Admin/ActivityGraphDetail';
import EngagementGraphsDetail from './screens/Admin/EngagementGraphsDetail';

import AppManager from './screens/Admin/AppManager';
import AppDetailScreen from './screens/Admin/AppDetailScreen';
import RevenueAnalyticsScreen from './screens/Admin/RevenueAnalyticsScreen';
import BannerRevenueAnalyticsScreen from './screens/Admin/BannerRevenueAnalyticsScreen';
import BarterFiSurveyManager from './screens/Admin/BarterFiSurveyManager';
import PioneerSummaryAnalytics from './screens/Admin/PioneerSummaryAnalytics';
import StudioAnalytics from './screens/Admin/StudioAnalytics';



// Social Screens
import PersistentHome from './components/navigation/PersistentHome';
import Login from './Login';
import AdminLogin from './screens/AdminLogin';
import Signup from './Signup';

import Profile from './Profile';
import ProfileMenuScreen from './screens/ProfileMenuScreen';

import UserProfile from './UserProfile';
import Search from './Search';
import DiscoverPeopleScreen from './screens/DiscoverPeopleScreen';
import SavedPosts from './SavedPosts';

import Notifications from './Notifications';
import ReceiptLogScreen from './screens/ReceiptLogScreen';
import SalesLeaderboardScreen from './screens/SalesLeaderboardScreen';
import FeedbackForm from './screens/Feedback/FeedbackForm';
import EditProfileScreen from './screens/EditProfileScreen';
import CommunityAvatars from './screens/CommunityAvatars';

// BarterFi Studio
import AvatarCreator from './studio/avatar/screens/AvatarCreator';
import { canAccessStudio } from './studio/studioAccess';

import CreatePostScreen from './screens/CreatePostScreen';
import CreatePollScreen from './screens/CreatePollScreen';

import MyWallet from './screens/MyWallet';
import SettingsScreen from './screens/SettingsScreen';
import BlockedAccountsScreen from './screens/BlockedAccountsScreen';
import AboutBarterFi from './screens/AboutBarterFi';
import TermsOfService from './screens/TermsOfService';

import CreateStoryScreen from './screens/CreateStoryScreen';
import StoryViewerScreen from './screens/StoryViewerScreen';
import BadgesLeaderboardScreen from './screens/BadgesLeaderboardScreen';
import BarterKingDetailScreen from './screens/BarterKingDetailScreen';
import MerchantStarDetailScreen from './screens/MerchantStarDetailScreen';
import CreatorFlameDetailScreen from './screens/CreatorFlameDetailScreen';
import PiGuardianDetailScreen from './screens/PiGuardianDetailScreen';
import PostDetailScreen from './screens/PostDetailScreen';
import NewsDetailScreen from './screens/NewsDetailScreen';
import StakeDetailsScreen from './screens/StakeDetailsScreen';
import PlansScreen from './screens/Plans/PlansScreen';
import EditPostScreen from './screens/EditPostScreen';
import ProfileBannerScreen from './screens/ProfileBanners/ProfileBannerScreen';

import MyAppsScreen from './screens/MyAppsScreen';
import RegisterAppScreen from './screens/RegisterAppScreen';
import MyAppsScreenDetail from './screens/MyAppsScreenDetail';
import AppTermsScreen from './screens/AppTermsScreen';
import PiAppsExploreScreen from './screens/PiAppsExploreScreen';

// Creator dashboard
import AnalyticsScreen from './screens/AnalyticsScreen';
import AnalyticsOverviewScreen from './screens/AnalyticsOverviewScreen';
import AnalyticsPerformanceScreen from './screens/AnalyticsPerformanceScreen';
import AnalyticsGrowthScreen from './screens/AnalyticsGrowthScreen';
import AnalyticsBoostScreen from './screens/AnalyticsBoostScreen';
import CreatorGraphsDetail from './screens/CreatorGraphsDetail';

// Chat Screens
import Chats from './Chats';
import ChatDetail from './ChatDetail';

// Merchant Screens
import RegisterMerchant from './RegisterMerchant';
import MerchantTermsScreen from './screens/MerchantTermsScreen';
import DeRegisterMerchant from './DeRegisterMerchant';
import MerchantStore from './screens/Marketplace/MerchantStore';
import AddProduct from './screens/Marketplace/AddProduct';
import EditStoreScreen from './screens/Marketplace/EditStoreScreen';
import ProductDetailScreen from './screens/Marketplace/ProductDetailScreen';
import EditProduct from './screens/Marketplace/EditProduct';

// Marketplace Screens
import Marketplace from './screens/Marketplace/Marketplace';
import CartOverview from './screens/Marketplace/CartOverview';
import SavedProductsScreen from './screens/Marketplace/SavedProductsScreen';
import PaymentConfirmationScreen from './screens/Marketplace/PaymentConfirmationScreen';
import CreateDisputeScreen from './screens/Disputes/CreateDisputeScreen';
import EscrowOverviewScreen from './screens/Marketplace/EscrowOverviewScreen';
import EscrowListScreen from './screens/Marketplace/EscrowListScreen';
import MarketSearch from './screens/MarketSearch/MarketSearch';
import BannerResults from './screens/Marketplace/BannerResults';
import BarterFiEscrow from './screens/BarterFiEscrow';
import DepartmentResults from './screens/Marketplace/DepartmentResults';
import DepartmentsAll from './screens/Marketplace/DepartmentsAll';
import StoresAll from './screens/Marketplace/StoresAll';

// Payment Screens
import MerchantPaymentScreen from './screens/payments/MerchantPaymentScreen';
import SponsorPaymentScreen from './screens/payments/SponsorPaymentScreen';

// Appeal & Dispute
import DisputeDecisionScreen from './screens/Disputes/DisputeDecisionScreen';
import AppealScreen from './screens/AppealScreen';

// Merchant Dashboard
import MerchantDashboard from './screens/Merchant/MerchantDashboard';
import ProductManager from './screens/Merchant/ProductManager';
import MerchantMetrics from './screens/Merchant/MerchantMetrics';
import StoreInsights from './screens/Merchant/StoreInsights';
import MerchantReviews from './screens/Merchant/MerchantReviews';
import MerchantPromotions from './screens/Merchant/MerchantPromotions';
import SelectProductToSponsorScreen from './screens/Merchant/SelectProductToSponsorScreen';
import CreateFlashSaleScreen from './screens/Merchant/CreateFlashSaleScreen';
import SelectProductForFlashSaleScreen from './screens/Merchant/SelectProductForFlashSaleScreen';

function App() {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('barterfi_user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const homeIdentityKey = user
    ? JSON.stringify([
        String(user._id || ''),
        String(user.username || '').trim().toLowerCase(),
      ])
    : 'signed-out';

  const [authChecked, setAuthChecked] = useState(false);

  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  const [cartCount, setCartCount] = useState(0);

  const updateAuthUser = useCallback((next) => {
    setUser(next);
    try {
      if (next) {
        localStorage.setItem('barterfi_user', JSON.stringify(next));
      } else {
        localStorage.removeItem('barterfi_user');
      }
    } catch {}

    try {
      window.dispatchEvent(new CustomEvent('user:updated', { detail: { user: next } }));
    } catch {}
  }, []);

  useEffect(() => {
    const bootstrapAuth = async () => {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('barterfi_token');

      if (!token) {
        updateAuthUser(null);
        setAuthChecked(true);
        return;
      }

      try {
        const res = await api.get('/auth/me');
        const freshUser = res.data?.user || res.data;

        console.log('✅ Auth restored from /auth/me:', freshUser);
        updateAuthUser(freshUser);
      } catch (err) {
        console.error('❌ Failed to restore auth user:', err?.response?.data || err.message);

        localStorage.removeItem('token');
        localStorage.removeItem('barterfi_token');
        localStorage.removeItem('barterfi_user');
        updateAuthUser(null);
      } finally {
        setAuthChecked(true);
      }
    };

    bootstrapAuth();
  }, [updateAuthUser]);

  useEffect(() => {
    let intervalId;

    const ping = async () => {
      if (!user?.username) return;

      try {
        await api.patch(`/users/ping/${user.username}`);
        console.log('📡 Pinged user activity');
      } catch (err) {
        console.warn('❌ Ping failed:', err.message);
      }
    };

    if (user?.username) {
      ping();
      intervalId = setInterval(ping, 5 * 60 * 1000);
    }

    return () => clearInterval(intervalId);
  }, [user?.username]);

  const handleLogin = (userInfo) => {
    updateAuthUser(userInfo);
  };

  const handleLogout = () => {
    localStorage.removeItem('barterfi_user');
    localStorage.removeItem('token');
    localStorage.removeItem('barterfi_token');
    setUser(null);
  };

  const handleMerchantRegister = (updatedUser) => {
    updateAuthUser(updatedUser);
  };

  if (!authChecked) {
    return null;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <I18nProvider defaultLang="en">
          <SuggestedStoriesDiscoveryProvider>
            <Router>
              <EscrowBannerProvider>
                <BannerProvider>
                  <DepartmentProvider>
                    <DiscoveryProvider currentUser={user}>
                      <ActiveStoresProvider>
                        <SavedProductsProvider>
                          <LikedProductsProvider>
                            <CartProvider user={user}>
                              <TipListProvider>

                                <PostProvider key={homeIdentityKey}>
                                  <ProfileProvider>
                                    <UserPostProvider>
                                      <MarketProvider user={user}>

<NotificationProvider user={user}>
  <MessageUnreadProvider user={user}>
    <NewsProvider>

                                              <SavedPostsProvider currentUser={user?.username}>

<PiAppsDiscoveryProvider user={user}>
  {user && (
    <PersistentHome
      key={homeIdentityKey}
      user={user}
      onUserUpdate={updateAuthUser}
    />
  )}

  <Routes>

                                            {!user ? (
                                              <>
                                                <Route path="/signup" element={<Signup onSignup={handleLogin} />} />
                                                <Route path="/admin-login" element={<AdminLogin onLogin={handleLogin} />} />
                                                <Route path="*" element={<Login onLogin={handleLogin} />} />
                                              </>
                                            ) : (
                                              <>
                                                <Route path="/" element={<Navigate to="/home" />} />


                                                <Route path="/home" element={null} />
                                                <Route path="/feedback" element={<FeedbackForm user={user} navigateBack={() => window.history.back()} />} />
                                                <Route path="/create-post" element={<CreatePostScreen user={user} />} />
                                                <Route path="/create-poll" element={<CreatePollScreen user={user} />} />
                                                <Route path="/create-story" element={<CreateStoryScreen user={user} />} />
                                                <Route path="/stories/:username" element={<StoryViewerScreen />} />
                                                <Route path="/marketplace" element={<Marketplace user={user} cartCount={cartCount} setCartCount={setCartCount} />} />
                                                <Route path="/market-search" element={<MarketSearch user={user} />} />
                                                <Route path="/marketplace/banner/:slug" element={<BannerResults user={user} />} />
                                                <Route path="/escrow" element={<BarterFiEscrow />} />
                                                <Route path="/marketplace/department/:key" element={<DepartmentResults user={user} />} />
                                                <Route path="/marketplace/departments" element={<DepartmentsAll />} />
                                                <Route path="/marketplace/stores" element={<StoresAll />} />

                                                <Route
                                                  path="/product/:id"
                                                  element={
                                                    <MerchantStoreProvider>
                                                      <ProductDetailScreen user={user} />
                                                    </MerchantStoreProvider>
                                                  }
                                                />

                                                <Route
                                                  path="/stake/:postId"
                                                  element={
                                                    <StakeDetailsScreen
                                                      effectiveNetwork="mainnet"
                                                      currentUser={user?.username}
                                                    />
                                                  }
                                                />

                                                <Route path="/post/:postId" element={<PostDetailScreen user={user} />} />

<Route
  path="/news/:articleId"
  element={
    <NewsDetailScreen user={user} />
  }
/>
                                                <Route path="/plans" element={<PlansScreen />} />
                                                <Route path="/posts/:id/edit" element={<EditPostScreen />} />

                                                <Route path="/analytics" element={<AnalyticsScreen user={user} />} />
                                                <Route path="/analytics/overview" element={<AnalyticsOverviewScreen user={user} />} />
                                                <Route path="/analytics/performance" element={<AnalyticsPerformanceScreen user={user} />} />
                                                <Route path="/analytics/growth" element={<AnalyticsGrowthScreen user={user} />} />
                                                <Route path="/analytics/boost" element={<AnalyticsBoostScreen user={user} />} />
                                                <Route path="/analytics/graphs/engagement" element={<CreatorGraphsDetail />} />

                                                <Route
                                                  path="/my-apps"
                                                  element={<MyAppsScreen user={user} onUserUpdate={updateAuthUser} />}
                                                />
                                                <Route
                                                  path="/my-apps/new"
                                                  element={<RegisterAppScreen user={user} onUserUpdate={updateAuthUser} />}
                                                />


<Route
  path="/app-terms"
  element={<AppTermsScreen />}
/>

                                                <Route path="/my-apps/:appId" element={<MyAppsScreenDetail user={user} />} />

                                                <Route path="/cart" element={<CartOverview user={user} />} />

<Route path="/profile" element={<Profile user={user} onLogout={handleLogout} onUserUpdate={updateAuthUser} />} />

<Route
  path="/profile-menu"
  element={<ProfileMenuScreen user={user} onLogout={handleLogout} />}
/>
                                                <Route path="/edit-profile" element={<EditProfileScreen user={user} onSave={updateAuthUser} />} />



<Route
  path="/studio/avatar"
  element={
    canAccessStudio(user) ? (
      <AvatarCreator
        key={String(user._id)}
        onProfileUpdated={(profileUpdates) => {
          if (
            !profileUpdates?._id ||
            String(profileUpdates._id) !== String(user._id)
          ) {
            throw new Error('Profile update belongs to another account.');
          }

          updateAuthUser({
            ...user,
            profilePic: profileUpdates.profilePic,
            equippedAvatarCreation:
              profileUpdates.equippedAvatarCreation ?? null,
          });
        }}
      />
    ) : (
      <Navigate to="/edit-profile" replace />
    )
  }
/>


                                                <Route path="/saved" element={<SavedPosts currentUser={user} />} />



<Route
  path="/profile/banners"
  element={
    <ProfileBannerScreen
      user={user}
      onUserUpdate={updateAuthUser}
    />
  }
/>

<Route path="/user/:username" element={<UserProfile currentUser={user} />} />
<Route path="/search" element={<Search user={user} />} />
<Route path="/discover-people" element={<DiscoverPeopleScreen user={user} />} />

<Route
  path="/community-avatars"
  element={
    <CommunityAvatars
      key={String(user._id)}
      user={user}
    />
  }
/>

<Route path="/pi-apps" element={<PiAppsExploreScreen user={user} />} />


<Route
  path="/notifications"
  element={
    <Notifications
      user={user}
      setUnreadNotifCount={setUnreadNotifCount}
    />
  }
/>


<Route path="/about-barterfi" element={<AboutBarterFi />} />
<Route path="/terms" element={<TermsOfService />} />
<Route path="/my-wallet" element={<MyWallet user={user} onLogout={handleLogout} />} />

<Route path="/settings" element={<SettingsScreen />} />
<Route path="/settings/blocked-accounts" element={<BlockedAccountsScreen />} />


                                                {/* Chat */}
                                                <Route path="/chats" element={<Chats currentUser={user} />} />
                                                <Route path="/chat/:username" element={<ChatDetail currentUser={user} />} />

                                                {/* Merchant */}
                                                <Route
                                                  path="/register-merchant"
                                                  element={<RegisterMerchant user={user} onMerchantRegister={handleMerchantRegister} />}
                                                />
                                                <Route
                                                  path="/deregister-merchant"
                                                  element={
                                                    <DeRegisterMerchant
                                                      user={user}
                                                      onDeregisterComplete={() => {
                                                        const updatedUser = { ...user, userType: 'pioneer' };
                                                        updateAuthUser(updatedUser);
                                                      }}
                                                    />
                                                  }
                                                />

                                                <Route
                                                  path="/marketplace/store/:businessUsername"
                                                  element={
                                                    <MerchantStoreProvider>
                                                      <MerchantStore user={user} />
                                                    </MerchantStoreProvider>
                                                  }
                                                />

                                                <Route
                                                  path="/store/:businessUsername"
                                                  element={
                                                    <MerchantStoreProvider>
                                                      <MerchantStore user={user} />
                                                    </MerchantStoreProvider>
                                                  }
                                                />

                                                <Route path="/edit-store" element={<EditStoreScreen user={user} />} />
                                                <Route path="/add-product" element={<AddProduct user={user} />} />
                                                <Route path="/edit-product/:id" element={<EditProduct user={user} />} />
                                                <Route path="/saved-products" element={<SavedProductsScreen user={user} />} />
                                                <Route path="/merchant-terms" element={<MerchantTermsScreen />} />

                                                {/* Merchant dashboard */}
                                                <Route path="/merchant/dashboard" element={<MerchantDashboard user={user} />} />
                                                <Route path="/merchant/products" element={<ProductManager user={user} />} />
                                                <Route path="/merchant/metrics" element={<MerchantMetrics user={user} />} />
                                                <Route path="/merchant/insights" element={<StoreInsights user={user} />} />
                                                <Route path="/merchant/reviews" element={<MerchantReviews user={user} />} />
                                                <Route path="/merchant/sponsorships" element={<MerchantPromotions user={user} />} />
                                                <Route path="/select-sponsor" element={<SelectProductToSponsorScreen user={user} />} />
                                                <Route path="/merchant/flash-sale" element={<SelectProductForFlashSaleScreen user={user} />} />
                                                <Route path="/merchant/flash-sale/new/:productId" element={<CreateFlashSaleScreen user={user} />} />

                                                {/* Payments */}
                                                <Route path="/checkout/:id" element={<PaymentConfirmationScreen user={user} />} />
                                                <Route path="/merchant-payment" element={<MerchantPaymentScreen user={user} />} />
                                                <Route path="/sponsor-payment/:id" element={<SponsorPaymentScreen user={user} />} />

                                                {/* Orders / Disputes */}
                                                <Route path="/receipts" element={<ReceiptLogScreen />} />
                                                <Route path="/leaderboard" element={<SalesLeaderboardScreen />} />
                                                <Route path="/badges-leaderboard" element={<BadgesLeaderboardScreen user={user} />} />

                                                {/* Leaderboard details */}
                                                <Route path="/badges/barter-king/:username" element={<BarterKingDetailScreen />} />
                                                <Route path="/barter-king/:username" element={<BarterKingDetailScreen />} />
                                                <Route path="/badges/merchant-star/:username" element={<MerchantStarDetailScreen />} />
                                                <Route path="/badges/creator-flame/:username" element={<CreatorFlameDetailScreen />} />
                                                <Route path="/creator-flame/:username" element={<CreatorFlameDetailScreen />} />
                                                <Route path="/badges/pi-guardian/:username" element={<PiGuardianDetailScreen />} />

                                                <Route path="/orders" element={<EscrowOverviewScreen user={user} />} />
                                                <Route path="/order/:orderId" element={<EscrowListScreen user={user} />} />
                                                <Route path="/dispute/:orderId" element={<CreateDisputeScreen user={user} />} />
                                                <Route path="/dispute-decision/:id" element={<DisputeDecisionScreen user={user} />} />

                                                {/* Appeals */}
                                                <Route path="/appeal" element={<AppealScreen />} />

                                                {/* Admin */}
                                                <Route path="/admin" element={<AdminRoute user={user}><AdminDashboard user={user} /></AdminRoute>} />


<Route
  path="/admin/survey"
  element={
    <AdminRoute user={user}>
      <BarterFiSurveyManager />
    </AdminRoute>
  }
/>

<Route
  path="/admin/pioneer-summary"
  element={
    <AdminRoute user={user}>
      <PioneerSummaryAnalytics />
    </AdminRoute>
  }
/>

<Route
  path="/admin/studio-analytics"
  element={
    <AdminRoute user={user}>
      <StudioAnalytics />
    </AdminRoute>
  }
/>


<Route
  path="/admin/apps"
  element={
    <AdminRoute user={user}>
      <AppManager user={user} />
    </AdminRoute>
  }
/>

<Route
  path="/admin/apps/:id"
  element={
    <AdminRoute user={user}>
      <AppDetailScreen user={user} />
    </AdminRoute>
  }
/>

                                                <Route path="/admin/disputes" element={<AdminRoute user={user}><DisputeManager user={user} /></AdminRoute>} />
                                                <Route path="/admin/disputes/:id" element={<DisputeDetailScreen />} />
                                                <Route path="/admin/reports" element={<ReportManager user={user} />} />
                                                <Route path="/admin/reports/:id" element={<ReportDetail user={user} />} />
                                                <Route path="/admin/merchants" element={<MerchantManager user={user} />} />
                                                <Route path="/admin/merchants/:id" element={<MerchantDetail user={user} />} />
                                                <Route path="/admin/appeals" element={<AppealManager />} />
                                                <Route path="/admin/appeals/:id" element={<AppealDetail />} />
                                                <Route path="/admin/users" element={<UserManager />} />
                                                <Route path="/admin/users/:id" element={<UserDetail />} />



<Route path="/admin/metrics" element={<Metrics />} />




<Route
  path="/admin/revenue-analytics"
  element={
    <AdminRoute user={user}>
      <RevenueAnalyticsScreen />
    </AdminRoute>
  }
/>

<Route
  path="/admin/revenue-analytics/banners"
  element={
    <AdminRoute user={user}>
      <BannerRevenueAnalyticsScreen />
    </AdminRoute>
  }
/>

<Route
  path="/admin/feed-config"
  element={
    <AdminRoute user={user}>
      <FeedConfigManager />
    </AdminRoute>
  }
/>


                                                <Route path="/admin/feedback" element={<AdminRoute user={user}><FeedbackManager user={user} /></AdminRoute>} />
                                                <Route path="/admin/feedback/:id" element={<FeedbackDetail />} />
                                                <Route path="/admin/sponsorships" element={<AdminRoute user={user}><SponsorshipManager user={user} /></AdminRoute>} />
                                                <Route path="/admin/sponsorships/:id" element={<SponsorshipDetail />} />
                                                <Route path="/admin/wallet" element={<AdminRoute user={user}><AdminWallet user={user} /></AdminRoute>} />
                                                <Route path="/admin/test-payment" element={<AdminTestPaymentScreen />} />
                                                <Route path="/admin/withdrawals" element={<WithdrawalManager />} />
                                                <Route path="/admin/withdrawals/:id" element={<WithdrawalDetail />} />
                                                <Route path="/admin/graphs" element={<GraphsManager />} />
                                                <Route path="/admin/graphs/detail" element={<GraphsDetail />} />
                                                <Route path="/admin/active-wallets" element={<ActiveWalletManager />} />
                                                <Route
                                                  path="/admin/lang-country"
                                                  element={
                                                    <AdminRoute user={user}>
                                                      <LangCountryStats />
                                                    </AdminRoute>
                                                  }
                                                />
                                                <Route
                                                  path="/admin/plans"
                                                  element={
                                                    <AdminRoute user={user}>
                                                      <PlansManager />
                                                    </AdminRoute>
                                                  }
                                                />
                                                <Route path="/admin/graphs/activity" element={<ActivityGraphDetail />} />
                                                <Route path="/admin/graphs/engagement" element={<EngagementGraphsDetail />} />
                                              </>
                                            )}
                                          </Routes>
                                            </PiAppsDiscoveryProvider>
                                        </SavedPostsProvider>

</NewsProvider>
  </MessageUnreadProvider>
</NotificationProvider>

                                      </MarketProvider>
                                    </UserPostProvider>
                                  </ProfileProvider>
                                </PostProvider>
                              </TipListProvider>
                            </CartProvider>
                          </LikedProductsProvider>
                        </SavedProductsProvider>
                      </ActiveStoresProvider>
                    </DiscoveryProvider>
                  </DepartmentProvider>
                </BannerProvider>
              </EscrowBannerProvider>
            </Router>
          </SuggestedStoriesDiscoveryProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
